/* =========================================================
   SPRUCE HYGIENE — predictive search
   =========================================================

   Live product suggestions under every search box on the site:
     - the header's icon-toggle panel
     - the shop sidebar box on collection pages
     - the form at the top of the search results page

   Each of those marks itself up as:

     <div data-predictive-search data-ps-url="{{ routes.predictive_search_url }}">
       <form …><input type="search" name="q" …></form>
       <div class="ps-panel" data-ps-panel hidden></div>
     </div>

   and this file wires up whichever ones are on the page. Adding a fourth
   search box later needs no change here.

   ---------------------------------------------------------
   IT DEGRADES, ALWAYS

   Suggestions are an accelerator, never a dependency. With JavaScript off, a
   failed request, or a slow network, every box stays an ordinary GET form that
   submits to /search. Nothing here calls preventDefault on a submit unless it
   is replacing it with a navigation of its own.
   ========================================================= */
(function () {
  'use strict';

  if (window.__sprucePredictiveSearch) return;
  window.__sprucePredictiveSearch = true;

  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 200;
  var LIMIT = 6;

  var instances = 0;

  /* ---------------------------------------------------------
     One search box
     --------------------------------------------------------- */
  function setup(root) {
    var input = root.querySelector('input[type="search"], input[name="q"]');
    var panel = root.querySelector('[data-ps-panel]');
    var form = root.querySelector('form');

    if (!input || !panel || !form) return;

    var url = root.getAttribute('data-ps-url') || '/search/suggest';
    var id = 'ps-' + (++instances);

    var timer = null;
    var controller = null;
    var lastQuery = '';
    var open = false;
    var activeIndex = -1;

    panel.id = id + '-panel';

    /* The rows live in two lists inside the panel, so the listbox has to be
       the panel itself — a role="option" needs a listbox ancestor, and putting
       it on either column would hide the other column's rows from the
       combobox. */
    panel.setAttribute('role', 'listbox');
    panel.setAttribute('aria-label', 'Search suggestions');

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', panel.id);
    input.setAttribute('autocomplete', 'off');

    /* ---- open / close ---- */

    function options() {
      return Array.prototype.slice.call(panel.querySelectorAll('.ps-item'));
    }

    function show() {
      if (open) return;
      open = true;
      panel.hidden = false;
      root.classList.add('ps-open');
      input.setAttribute('aria-expanded', 'true');
    }

    function hide() {
      if (!open) return;
      open = false;
      panel.hidden = true;
      root.classList.remove('ps-open');
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    }

    function clear() {
      panel.innerHTML = '';
      hide();
    }

    /* ---- highlight ---- */

    function highlight(index) {
      var items = options();

      if (!items.length) {
        activeIndex = -1;
        input.removeAttribute('aria-activedescendant');
        return;
      }

      /* Wrap around, and allow -1 meaning "back in the text field", which is
         what a reader expects from ArrowUp on the first item. */
      if (index < -1) index = items.length - 1;
      if (index >= items.length) index = -1;

      activeIndex = index;

      items.forEach(function (item, i) {
        var on = i === index;
        item.classList.toggle('is-active', on);
        item.setAttribute('aria-selected', on ? 'true' : 'false');

        if (on) {
          input.setAttribute('aria-activedescendant', item.id);

          /* Keep the highlighted row in view when the list scrolls. */
          if (item.scrollIntoView) {
            item.scrollIntoView({ block: 'nearest' });
          }
        }
      });

      if (index === -1) {
        input.removeAttribute('aria-activedescendant');
      }
    }

    /* ---- fetch ---- */

    function render(html) {
      panel.innerHTML = html;

      /* The section emits ids of its own, and two search boxes on one page
         would then both contain #ps-opt-1 — duplicate ids break
         aria-activedescendant, which resolves against the whole document. So
         every instance renumbers what it just received. */
      options().forEach(function (item, i) {
        item.id = id + '-opt-' + (i + 1);
      });

      activeIndex = -1;

      if (panel.children.length) {
        show();
      } else {
        hide();
      }
    }

    function search(query) {
      /* Abort whatever is in flight. Without this, a slow response for "ha"
         can land after a fast one for "hand towel" and overwrite the newer
         results with older ones — the classic autocomplete race. */
      if (controller) controller.abort();

      controller = ('AbortController' in window) ? new AbortController() : null;

      var endpoint = url
        + (url.indexOf('?') === -1 ? '?' : '&')
        + 'q=' + encodeURIComponent(query)
        + '&section_id=predictive-search'
        /* Completions and collections as well as products — the section lays
           them out as the two columns of the dropdown. Order here does not
           set the order on screen; the section decides that. */
        + '&resources[type]=query,collection,product'
        + '&resources[limit]=' + LIMIT;

      var opts = controller ? { signal: controller.signal } : {};

      root.classList.add('ps-loading');

      fetch(endpoint, opts)
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (text) {
          /* A late response for a query the user has already moved on from is
             discarded even if the abort did not land first. */
          if (query !== lastQuery) return;

          var doc = new DOMParser().parseFromString(text, 'text/html');
          var section = doc.querySelector('#shopify-section-predictive-search');

          render(section ? section.innerHTML : '');
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;

          /* Suggestions are an accelerator. If they fail, say nothing and
             leave the reader with a working search form. */
          clear();
        })
        .then(function () {
          root.classList.remove('ps-loading');
        });
    }

    /* ---- input ---- */

    input.addEventListener('input', function () {
      var query = input.value.trim();
      lastQuery = query;

      if (timer) clearTimeout(timer);

      if (query.length < MIN_CHARS) {
        if (controller) controller.abort();
        clear();
        return;
      }

      timer = setTimeout(function () { search(query); }, DEBOUNCE_MS);
    });

    input.addEventListener('focus', function () {
      if (panel.children.length && input.value.trim().length >= MIN_CHARS) show();
    });

    /* ---- keyboard ---- */

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        clear();
        return;
      }

      if (!open) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        highlight(activeIndex + 1);
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        highlight(activeIndex - 1);
        return;
      }

      if (e.key === 'Enter') {
        /* Only intercept Enter when the reader has actually chosen a row.
           Otherwise this is a normal search submit and the form should handle
           it — hijacking it would break "type a phrase, press Enter". */
        var items = options();

        if (activeIndex >= 0 && items[activeIndex]) {
          var link = items[activeIndex].querySelector('a[href]');

          if (link) {
            e.preventDefault();
            window.location.href = link.href;
          }
        }
        return;
      }

      if (e.key === 'Tab') {
        hide();
      }
    });

    /* Pointer hover should agree with keyboard position, or the two fight. */
    panel.addEventListener('mousemove', function (e) {
      var item = e.target.closest ? e.target.closest('.ps-item') : null;
      if (!item) return;

      var index = options().indexOf(item);
      if (index !== -1 && index !== activeIndex) highlight(index);
    });

    /* Closing on blur directly would fire before a click on a suggestion
       registers, so the click is lost. Document-level pointerdown is the
       reliable version. */
    document.addEventListener('pointerdown', function (e) {
      if (!root.contains(e.target)) hide();
    });

    form.addEventListener('submit', function () {
      hide();
    });
  }

  /* ---------------------------------------------------------
     Boot, and re-boot after theme-editor section renders
     --------------------------------------------------------- */
  function init(scope) {
    (scope || document).querySelectorAll('[data-predictive-search]').forEach(function (root) {
      if (root.__psReady) return;
      root.__psReady = true;
      setup(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* The theme editor replaces section markup wholesale, which throws away the
     listeners bound above. Same reason theme.js uses delegated listeners. */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
}());
